import {
  context,
  trace,
  SpanStatusCode,
  TraceFlags,
  type Context,
  type Span,
  type SpanContext,
} from '@opentelemetry/api'
import type { FetchService, HostTracing, SpanAttributes, SpanException, SpanStatus } from 'runtimes-shared'
import { getActiveScreenSpan } from '../../otel/activeScreenSpan'

const tracer = trace.getTracer('entity-console')

const INVALID_TRACE_ID = '0'.repeat(32)

// Build/parse the W3C traceparent directly from the span context rather than via the global propagator,
// which isn't reliably configured to inject in this app (it returned no traceparent).
const traceparentOfSpan = (span: Span): string | undefined => {
  const sc = span.spanContext()
  if (!sc?.traceId || sc.traceId === INVALID_TRACE_ID) return undefined
  const flags = (sc.traceFlags & TraceFlags.SAMPLED) === TraceFlags.SAMPLED ? '01' : '00'
  return `00-${sc.traceId}-${sc.spanId}-${flags}`
}

const contextFrom = (traceparent: string | undefined, fallback: Context): Context => {
  if (!traceparent) return fallback
  const [, traceId, spanId, flags] = traceparent.split('-')
  if (!traceId || !spanId || traceId === INVALID_TRACE_ID) return fallback
  const spanContext: SpanContext = {
    traceId,
    spanId,
    traceFlags: flags === '01' ? TraceFlags.SAMPLED : TraceFlags.NONE,
    isRemote: true,
  }
  return trace.setSpanContext(fallback, spanContext)
}

export type RuntimeHostTracing = {
  /** Tracing capability to hand to `RuntimeHost.run({ tracing })`. */
  tracing: HostTracing
  /** `fetchService` wrapper that runs each request under the supplied parent so it nests + propagates. */
  fetchService: FetchService
  /** Run `fn` under the OTel context of `parent` (or the session root) so its spans/fetches nest. */
  withinParent<T>(parent: string | undefined, fn: () => T): T
  /** End the session span (and any spans the runtime left open). Call on teardown/refresh. */
  end(): void
}

/**
 * Open a session root span for one runtime instance and return the tracing capability + a parent-aware
 * `fetchService` wrapper around `baseFetchService`.
 */
export const createRuntimeHostTracing = (
  sessionName: string,
  attributes: SpanAttributes,
  baseFetchService: FetchService
): RuntimeHostTracing => {
  // Parent the session under the current screen span so the whole "open asset" journey is one trace. The
  // OTel context manager doesn't make the screen span ambient across React renders, hence the explicit lookup.
  const screenSpan = getActiveScreenSpan()
  const baseContext = screenSpan ? trace.setSpan(context.active(), screenSpan) : context.active()
  const sessionSpan = tracer.startSpan(sessionName, { attributes }, baseContext)
  const sessionContext = trace.setSpan(baseContext, sessionSpan)
  const rootTraceparent = traceparentOfSpan(sessionSpan)

  const live = new Map<string, Span>()
  let fallbackId = 0

  const startSpan = (name: string, attrs?: SpanAttributes, parent?: string): string => {
    const parentContext = contextFrom(parent, sessionContext)
    const span = tracer.startSpan(name, attrs ? { attributes: attrs } : undefined, parentContext)
    // Key by the new span's traceparent; fall back to a local id when tracing is disabled (no-op spans).
    const handle = traceparentOfSpan(span) ?? `local-${fallbackId++}`
    live.set(handle, span)
    return handle
  }

  const endSpan = (handle: string, status?: SpanStatus, exception?: SpanException): void => {
    const span = live.get(handle)
    if (!span) return
    live.delete(handle)
    // Record before setting status/ending so the failing span carries the exception event, not just ERROR.
    if (exception)
      span.recordException({ name: exception.name, message: exception.message ?? '', stack: exception.stack })
    if (status === 'error') span.setStatus({ code: SpanStatusCode.ERROR })
    span.end()
  }

  const fetchService: FetchService = (service, path, init, parent) =>
    context.with(contextFrom(parent, sessionContext), () => baseFetchService(service, path, init))

  const withinParent = <T>(parent: string | undefined, fn: () => T): T =>
    context.with(contextFrom(parent, sessionContext), fn)

  const end = (): void => {
    for (const span of live.values()) span.end()
    live.clear()
    sessionSpan.end()
  }

  return { tracing: { rootTraceparent, startSpan, endSpan }, fetchService, withinParent, end }
}
